/**
 * Short, action-shaped suggestions for the validation legend's per-issue
 * rows. The backend's {@link ValidationErrorDto#message} is a one-liner
 * spec (`Task "Repair" has no upstream input`); the hint adds the
 * "and here's the next mouse gesture" half (`Drag a wire from an
 * upstream node into this port`) so the user can act without first
 * mapping the rule code to a fix.
 *
 * <p>Codes mirror the backend rules exactly (see
 * {@code control-plane/.../process/validation/rules/*.java}). Returns
 * {@code null} for unknown codes so a future rule lights up in the
 * legend without an FE change — the row still renders the backend
 * message verbatim, just without a hint underline.</p>
 */
const HINTS: Readonly<Record<string, string>> = {
  // PortConnectionRule (publish-only — a draft still runs half-wired)
  INPUT_PORT_DISCONNECTED:
    'Before publishing, drag a wire from an upstream node into this port',
  OUTPUT_PORT_DISCONNECTED:
    'Before publishing, connect this port to a downstream task',

  // PortTypeMatchRule
  PORT_TYPE_MISMATCH: 'Connect ports of the same data type',

  // HasInputNodeRule / HasOutputNodeRule (publish-only)
  MISSING_INPUT_NODE:
    'Add an Input node before publishing so the process has somewhere to start',
  MISSING_OUTPUT_NODE:
    'Add an Output node before publishing so the process can produce a final result',

  // GatePortsConnectedRule / GateNodesConfiguredRule
  GATE_PORT_DISCONNECTED:
    'Wire both the approved and rejected branches before publishing',
  GATE_REVIEW_INSTRUCTIONS_MISSING: 'Add review instructions to this gate',
  GATE_AUTO_AGENT_MISSING:
    'Pick a reviewer skill for this gate or switch the mode back to Manual',

  // TaskNodesConfiguredRule
  TASK_INSTRUCTION_MISSING: 'Add an instruction so the task knows what to do',
  TASK_AGENT_MISSING: 'Pick a skill for this task',

  // LocalAgentCapabilityRule (advisory — never blocks publish)
  LOCAL_AGENT_NO_CAPABLE_ASSIGNEE:
    'Assign someone whose local runner has the app, or it will wait at run time',

  // AllNodesReachableRule (publish-only)
  NODE_UNREACHABLE_FROM_INPUT:
    'Before publishing, wire an upstream path so this node can be reached from an Input',
  NODE_CANNOT_REACH_OUTPUT:
    'Before publishing, wire a downstream path so this node feeds into an Output',

  // LoopHasGateRule
  LOOP_WITHOUT_GATE: 'Add a Gate node to break the loop with a review step',

  // EdgePortIntegrityRule
  EDGE_NODE_MISSING: 'Remove this connection — one of its endpoints is gone',
  EDGE_PORT_MISSING: 'Remove this connection — its port no longer exists',
  EDGE_PORT_NODE_MISMATCH: 'Remove this connection and re-wire from scratch',
  EDGE_PORT_DIRECTION_INVALID:
    'Connections must run from an output port to an input port',
  EDGE_SELF_LOOP: 'A node can\u2019t connect back to itself — wire it through a gate instead',

  // PortLabelsRule
  PORT_LABEL_EMPTY: 'Give this port a name',
  PORT_LABEL_DUPLICATE: 'Use a unique label for each port on the node',

  // PortBoundsRule
  PORT_COUNT_OUT_OF_BOUNDS:
    'Adjust the number of ports on this node to match the schema',

  // Synthesised run-failure rows (see ProcessStatusLegend#RUN_FAILED_KIND).
  // Backend isn't aware of this code — it's a frontend-only sentinel that
  // surfaces persisted node failures in the same legend; hint nudges the
  // user toward the dock panel where the full agent transcript lives.
  RUN_FAILED: 'Open this node to see the agent transcript and full error',
};

/**
 * Look up the suggestion line for a backend validation code. Returns
 * {@code null} when the code is unknown so the row renders without a
 * second line (the backend message stays the source of truth).
 */
export function validationHint(kind: string): string | null {
  return HINTS[kind] ?? null;
}
